import { useTranslation } from "react-i18next"

import type { FairUsePersonIdentity, FairUseTier } from "@/api/admin/fair-use"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { FAIR_USE_ASSIGNABLE_TIERS, fairUseDisplayName } from "./fair-use-utils"

type FairUsePlanSelectProps<Row extends FairUsePersonIdentity> = {
  /**
   * The identity half of a row only — a plan is a standing assignment, not a
   * month's arithmetic. That is what lets somebody who has spent NOTHING this
   * month be put on Pro: their row carries no usage at all.
   */
  row: Row
  /** Disabled while an assignment is in flight, or for a read-only admin. */
  disabled?: boolean
  onChange: (row: Row, tier: FairUseTier) => void
}

/**
 * The per-user plan assignment, as one cell.
 *
 * Assignment is the delegable half of fair use and belongs with the people who
 * are assigned — Users & licences — not on the operations table, which answers
 * "who is spending this month". The same control was inline in the operations
 * table until round 4; it lives here so exactly one surface can write a user's
 * plan.
 */
export function FairUsePlanSelect<Row extends FairUsePersonIdentity>({
  row,
  disabled,
  onChange,
}: FairUsePlanSelectProps<Row>) {
  const { t } = useTranslation()
  const plan = row.tier ?? "standard"
  const displayName = fairUseDisplayName(row)

  return (
    <Select
      disabled={disabled}
      value={plan}
      onValueChange={(next) => {
        if (next === plan) return
        onChange(row, next as FairUseTier)
      }}
    >
      <SelectTrigger
        className="h-9 w-[130px]"
        aria-label={t("adminDashboard.fairUse.ops.table.planFor", "Plan for {{user}}", {
          user: displayName,
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {/* A legacy per-user amount has no plan to switch back to, so it stays
            listed while it is the current value — otherwise the Select would
            render blank. */}
        {plan === "custom" && (
          <SelectItem value="custom">
            {t("adminDashboard.fairUse.plans.custom", "Custom")}
          </SelectItem>
        )}
        {FAIR_USE_ASSIGNABLE_TIERS.map((tier) => (
          <SelectItem key={tier} value={tier}>
            {t(`adminDashboard.fairUse.plans.${tier}`, tier)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
